/**
 * Brand marks for the three platforms we publish to, drawn in their official colours.
 * Inline SVG rather than a webfont or a remote asset: no extra request, no CSP surface,
 * and they stay crisp at the small sizes the footer and landing pills use.
 * Trademark note: these are the platforms' own marks, used only to name the destination
 * a post goes to — the footer carries the "not affiliated with" disclosure.
 */

import type { Platform } from "../lib/types";

type IconProps = { className?: string };

const BASE = { viewBox: "0 0 24 24", "aria-hidden": true, focusable: "false" } as const;

export function FacebookIcon({ className = "platform-icon" }: IconProps) {
  return (
    <svg {...BASE} className={className} role="img">
      <path
        fill="#1877f2"
        d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.12 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07Z"
      />
      <path
        fill="#fff"
        d="m16.67 15.56.53-3.49h-3.33V9.82c0-.96.47-1.89 1.96-1.89h1.51V4.96s-1.38-.24-2.69-.24c-2.74 0-4.53 1.67-4.53 4.69v2.66H7.08v3.49h3.04V24a12.1 12.1 0 0 0 3.75 0v-8.44h2.8Z"
      />
    </svg>
  );
}

export function InstagramIcon({ className = "platform-icon" }: IconProps) {
  return (
    <svg {...BASE} className={className} role="img">
      <defs>
        <linearGradient id="simino-ig-mark" x1="2" y1="22" x2="22" y2="2">
          <stop offset="0" stopColor="#ffae42" />
          <stop offset=".28" stopColor="#f5673c" />
          <stop offset=".62" stopColor="#ed3c74" />
          <stop offset="1" stopColor="#793bdb" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="24" height="24" rx="6.5" fill="url(#simino-ig-mark)" />
      <rect x="5.4" y="5.4" width="13.2" height="13.2" rx="4.2" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="3.2" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="16.9" cy="7.2" r="1.2" fill="#fff" />
    </svg>
  );
}

export function YouTubeIcon({ className = "platform-icon" }: IconProps) {
  return (
    <svg {...BASE} className={className} role="img">
      <path
        fill="#ff0000"
        d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.5A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81Z"
      />
      <path fill="#fff" d="M9.55 15.57V8.43L15.82 12l-6.27 3.57Z" />
    </svg>
  );
}

/**
 * The mark for a platform, by name.
 *
 * Three places were keeping their own copy of this three-line map — the target
 * picker, the media fit strip and the shell's account chips — which is three
 * places to edit when a fourth network arrives.
 */
export const PLATFORM_ICON: Record<Platform, (p: IconProps) => JSX.Element> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  youtube: YouTubeIcon,
};
